// ============================================================================
// Store Configuration API Service
// Connects to hcp-admin-backend APIs for Site Rates, Taxes, and Fees
// Uses fetchWithAuth for automatic token refresh on 401
// ============================================================================

import { get, post, put, patch, del } from './auth';

// ============================================================================
// Types
// ============================================================================

export interface StorePricing {
  id: number;
  store_id: number;
  site_type_id?: number;
  site_type_name?: string;
  price: number;
  effective_date?: string;
  end_date?: string;
  discount: number;
  active: boolean;
  description?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
}

export interface StorePricingCreate {
  store_id: number;
  site_type_id?: number;
  price: number;
  effective_date?: string;
  end_date?: string;
  discount?: number;
  active?: boolean;
  description?: string;
}

export interface StorePricingUpdate {
  site_type_id?: number;
  price?: number;
  effective_date?: string;
  end_date?: string;
  discount?: number;
  active?: boolean;
  description?: string;
}

export interface StoreTax {
  id: number;
  store_id: number;
  tax_name: string;
  tax_code?: string;
  rate_type: 'percentage' | 'flat';
  rate_value: number;
  unit_type: 'once' | 'night' | 'person' | 'site';
  effective_from?: string;
  effective_until?: string;
  active: boolean;
  tax_authority?: string;
  remittance_frequency?: 'monthly' | 'quarterly' | 'annually';
  description?: string;
  priority: number;
  created_at?: string;
  updated_at?: string;
}

export interface StoreTaxCreate {
  store_id: number;
  tax_name: string;
  tax_code?: string;
  rate_type: 'percentage' | 'flat';
  rate_value: number;
  unit_type?: 'once' | 'night' | 'person' | 'site';
  effective_from?: string;
  effective_until?: string;
  active?: boolean;
  tax_authority?: string;
  remittance_frequency?: 'monthly' | 'quarterly' | 'annually';
  description?: string;
  priority?: number;
}

export interface StoreTaxUpdate {
  tax_name?: string;
  tax_code?: string;
  rate_type?: 'percentage' | 'flat';
  rate_value?: number;
  unit_type?: 'once' | 'night' | 'person' | 'site';
  effective_from?: string;
  effective_until?: string;
  active?: boolean;
  tax_authority?: string;
  remittance_frequency?: 'monthly' | 'quarterly' | 'annually';
  description?: string;
  priority?: number;
}

export interface StoreCharge {
  id: number;
  store_id: number;
  item: string;
  amount: number;
  flat: boolean;
  active: boolean;
  charge_category: string;
  fee_category_id?: number;
  fee_trigger?: string;
  frequency: string;
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
}

export interface StoreChargeCreate {
  item: string;
  amount: number;
  flat?: boolean;
  active?: boolean;
  charge_category?: string;
  fee_category_id?: number;
  fee_trigger?: string;
  frequency?: string;
}

export interface StoreChargeUpdate {
  item?: string;
  amount?: number;
  flat?: boolean;
  active?: boolean;
  charge_category?: string;
  fee_category_id?: number;
  fee_trigger?: string;
  frequency?: string;
}

// ============================================================================
// Store Pricing (Site Rates) API
// ============================================================================

export async function getStorePricing(storeId?: number, activeOnly?: boolean): Promise<StorePricing[]> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  if (activeOnly !== undefined) params.append('active_only', activeOnly.toString());
  const queryString = params.toString();
  return get(`/store-pricing/${queryString ? '?' + queryString : ''}`);
}

export async function getStorePricingById(pricingId: number): Promise<StorePricing> {
  return get(`/store-pricing/${pricingId}`);
}

export async function createStorePricing(data: StorePricingCreate): Promise<StorePricing> {
  return post('/store-pricing/', data);
}

export async function updateStorePricing(pricingId: number, data: StorePricingUpdate): Promise<StorePricing> {
  return put(`/store-pricing/${pricingId}`, data);
}

export async function deleteStorePricing(pricingId: number): Promise<void> {
  return del(`/store-pricing/${pricingId}`);
}

export async function toggleStorePricing(pricingId: number): Promise<StorePricing> {
  return put(`/store-pricing/${pricingId}/toggle`, {});
}

// ============================================================================
// Store Taxes API
// ============================================================================

export async function getStoreTaxes(storeId?: number, activeOnly?: boolean): Promise<StoreTax[]> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  if (activeOnly !== undefined) params.append('active_only', activeOnly.toString());
  const queryString = params.toString();
  return get(`/store-taxes/${queryString ? '?' + queryString : ''}`);
}

export async function getStoreTaxById(taxId: number): Promise<StoreTax> {
  return get(`/store-taxes/${taxId}`);
}

export async function createStoreTax(data: StoreTaxCreate): Promise<StoreTax> {
  return post('/store-taxes/', data);
}

export async function updateStoreTax(taxId: number, data: StoreTaxUpdate): Promise<StoreTax> {
  return put(`/store-taxes/${taxId}`, data);
}

export async function deleteStoreTax(taxId: number): Promise<void> {
  return del(`/store-taxes/${taxId}`);
}

export async function toggleStoreTax(taxId: number): Promise<StoreTax> {
  // Use PATCH for toggle endpoint
  const { fetchWithAuth } = await import('./auth');
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';
  return fetchWithAuth(`${API_BASE_URL}/store-taxes/${taxId}/toggle`, {
    method: 'PATCH',
  });
}

// ============================================================================
// Store Charges (Fees) API
// ============================================================================

export async function getStoreCharges(storeId?: number): Promise<StoreCharge[]> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  const queryString = params.toString();
  return get(`/charges/${queryString ? '?' + queryString : ''}`);
}

export async function getStoreChargeById(chargeId: number): Promise<StoreCharge> {
  return get(`/charges/${chargeId}`);
}

export async function createStoreCharge(data: StoreChargeCreate, storeId?: number): Promise<StoreCharge> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  const queryString = params.toString();
  return post(`/charges/${queryString ? '?' + queryString : ''}`, data);
}

export async function updateStoreCharge(chargeId: number, data: StoreChargeUpdate): Promise<StoreCharge> {
  return patch(`/charges/${chargeId}`, data);
}

export async function deleteStoreCharge(chargeId: number): Promise<void> {
  return del(`/charges/${chargeId}`);
}

// ============================================================================
// Fee Triggers Lookup
// ============================================================================

export interface FeeTriggerOption {
  value: string;
  label: string;
}

export async function getFeeTriggers(): Promise<FeeTriggerOption[]> {
  return get('/charges/fee-triggers/');
}

// ============================================================================
// Cancellation Policies API (Read-Only)
// ============================================================================

export interface CancellationPolicyRead {
  id: number;
  name: string;
  description?: string;
  rules: {
    days_before_checkin_min: number;
    days_before_checkin_max?: number;
    fee_type: string;
    fee_value: number;
    description?: string;
  }[];
  modification_fee_type: string;
  modification_fee_value: number;
  no_refund_after_date?: string;
  is_active: boolean;
}

export async function getCancellationPolicies(storeId?: number): Promise<CancellationPolicyRead[]> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  params.append('active_only', 'true');
  const queryString = params.toString();
  return get(`/cancellation-policies/${queryString ? '?' + queryString : ''}`);
}

// ============================================================================
// Discount Codes
// ============================================================================

export type DiscountType = 'percentage' | 'fixed' | 'per_night' | 'free_nights';

export interface DiscountCode {
  id: number;
  store_id?: number | null;
  code: string;
  description?: string | null;
  discount_type: DiscountType;
  discount_value: number;
  min_nights?: number | null;
  min_booking_amount?: number | null;
  max_discount_amount?: number | null;
  max_uses_per_customer?: number | null;
  valid_from?: string | null;        // booking window start (YYYY-MM-DD)
  valid_until?: string | null;       // booking window end
  stay_valid_from?: string | null;   // stay window start (checked against check-in)
  stay_valid_until?: string | null;  // stay window end
  active: boolean;
  usage_count: number;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface DiscountCodeCreate {
  store_id?: number;
  code: string;
  description?: string;
  discount_type: DiscountType;
  discount_value: number;
  min_nights?: number | null;
  min_booking_amount?: number | null;
  max_discount_amount?: number | null;
  max_uses_per_customer?: number | null;
  valid_from?: string | null;
  valid_until?: string | null;
  stay_valid_from?: string | null;
  stay_valid_until?: string | null;
}

// Backend update schema does not allow changing code / discount_type / store_id
export interface DiscountCodeUpdate {
  description?: string;
  discount_value?: number;
  min_nights?: number | null;
  min_booking_amount?: number | null;
  max_discount_amount?: number | null;
  max_uses_per_customer?: number | null;
  valid_from?: string | null;
  valid_until?: string | null;
  stay_valid_from?: string | null;
  stay_valid_until?: string | null;
  active?: boolean;
}

export async function getDiscountCodes(storeId?: number): Promise<DiscountCode[]> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  const queryString = params.toString();
  return get(`/discount-codes/${queryString ? '?' + queryString : ''}`);
}

export async function getDiscountCodeById(discountCodeId: number): Promise<DiscountCode> {
  return get(`/discount-codes/${discountCodeId}`);
}

export async function createDiscountCode(data: DiscountCodeCreate): Promise<DiscountCode> {
  return post('/discount-codes/', data);
}

export async function updateDiscountCode(discountCodeId: number, data: DiscountCodeUpdate): Promise<DiscountCode> {
  return put(`/discount-codes/${discountCodeId}`, data);
}

export async function deleteDiscountCode(discountCodeId: number): Promise<void> {
  return del(`/discount-codes/${discountCodeId}`);
}

// ============================================================================
// Named Export Object (for easier usage in components)
// ============================================================================

export const configService = {
  // Store Pricing (Site Rates)
  getStorePricing,
  getStorePricingById,
  createStorePricing,
  updateStorePricing,
  deleteStorePricing,
  toggleStorePricing,

  // Store Taxes
  getStoreTaxes,
  getStoreTaxById,
  createStoreTax,
  updateStoreTax,
  deleteStoreTax,
  toggleStoreTax,

  // Store Charges (Fees)
  getStoreCharges,
  getStoreChargeById,
  createStoreCharge,
  updateStoreCharge,
  deleteStoreCharge,

  // Lookups
  getFeeTriggers,

  // Cancellation Policies (Read-Only)
  getCancellationPolicies,

  // Discount Codes
  getDiscountCodes,
  getDiscountCodeById,
  createDiscountCode,
  updateDiscountCode,
  deleteDiscountCode,
};

export default configService;
